import logo from "@/assets/tecopos-logo.png";
import {
  ShoppingCart,
  Package,
  Globe,
  CreditCard,
  Wallet,
  BarChart3,
  Clock,
  Users,
  Receipt,
  Building2,
  Sparkles,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { useLandingContent, type EcosystemItem } from "@/features/admin/content/landingContent";

const ICONS: Record<EcosystemItem["iconKey"], LucideIcon> = {
  cart: ShoppingCart,
  package: Package,
  globe: Globe,
  card: CreditCard,
  wallet: Wallet,
  chart: BarChart3,
  clock: Clock,
  users: Users,
  receipt: Receipt,
  building: Building2,
  sparkles: Sparkles,
};

const isExternal = (h: string) => /^https?:\/\//.test(h);

/**
 * Ecosistema TecoPOS. Cada módulo es editable; al pasar el cursor se abre un
 * popover flotante DISCRETO junto al módulo (no bloquea la pantalla) con la
 * descripción y, opcionalmente, un botón con enlace.
 */
export function Ecosystem() {
  const { ecosystem } = useLandingContent();
  const n = ecosystem.length || 1;

  return (
    <section id="ecosistema" className="relative py-24 lg:py-32 bg-neutral-50 overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_50%_40%,rgba(0,98,255,0.07),transparent_70%)]" />
      <div className="max-w-7xl mx-auto px-5 lg:px-6">
        <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-20 reveal">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Ecosistema TecoPOS</span>
          <h2 className="text-3xl lg:text-6xl font-bold tracking-tight mt-3 leading-[1.05]">
            No necesitas 10 sistemas.
            <br />
            <span className="text-muted-foreground">Necesitas uno que lo conecte todo.</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-5">Pasa el cursor sobre cada módulo para ver qué hace.</p>
        </div>

        <div className="relative mx-auto aspect-square max-w-[640px] reveal">
          <div className="absolute inset-[12%] rounded-full border border-dashed border-black/10" />
          <div className="absolute inset-[4%] rounded-full border border-dashed border-black/10" />

          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
            {ecosystem.map((item, i) => {
              const rad = (((360 / n) * i) * Math.PI) / 180;
              const x = 50 + Math.cos(rad) * 42;
              const y = 50 + Math.sin(rad) * 42;
              return <line key={item.id} x1="50" y1="50" x2={x} y2={y} stroke="currentColor" strokeWidth="0.2" className="text-primary/40 animate-dash" />;
            })}
          </svg>

          {/* Hub central */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <div className="relative">
              <span className="absolute inset-0 rounded-full bg-primary/30 animate-pulse-ring" />
              <span className="absolute inset-0 rounded-full bg-primary/20 animate-pulse-ring [animation-delay:1.2s]" />
              <div className="relative size-16 lg:size-20 rounded-full bg-white grid place-items-center shadow-2xl shadow-primary/40 border border-primary/10">
                <img src={logo} alt="TecoPOS" className="size-8 lg:size-10 object-contain" />
              </div>
            </div>
          </div>

          {/* Módulos */}
          {ecosystem.map((item, i) => {
            const Icon = ICONS[item.iconKey] ?? Package;
            const rad = (((360 / n) * i) * Math.PI) / 180;
            const x = 50 + Math.cos(rad) * 44;
            const y = 50 + Math.sin(rad) * 44;
            const openLeft = x >= 50; // en la mitad derecha, el popover abre hacia la izquierda (hacia el centro)
            return (
              <div key={item.id} className="group absolute z-20 -translate-x-1/2 -translate-y-1/2 hover:z-50" style={{ left: `${x}%`, top: `${y}%` }}>
                <div className="flex flex-col items-center gap-2 animate-float" style={{ animationDelay: `${i * 0.25}s` }}>
                  <div className="size-12 lg:size-14 rounded-2xl bg-white border border-black/5 shadow-lg grid place-items-center text-primary group-hover:scale-110 group-hover:border-primary/30 transition-all cursor-default">
                    {item.iconSvg ? (
                      <img src={item.iconSvg} alt="" className="size-5 object-contain" />
                    ) : (
                      <Icon className="size-5" />
                    )}
                  </div>
                  <span className="text-[10px] lg:text-xs font-semibold text-foreground bg-white/80 backdrop-blur px-2 py-0.5 rounded-full border border-black/5 whitespace-nowrap">
                    {item.label}
                  </span>
                </div>

                {/* Popover flotante discreto (abre hacia el centro) */}
                <div
                  className={`pointer-events-none invisible absolute top-1/2 z-30 w-52 -translate-y-1/2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:pointer-events-auto ${
                    openLeft ? "right-full mr-3" : "left-full ml-3"
                  }`}
                >
                  <div className="rounded-2xl border border-black/5 bg-white p-4 text-left shadow-xl">
                    <p className="text-sm font-bold text-foreground">{item.label}</p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.description}</p>
                    {item.buttonLabel && item.buttonHref && (
                      <a
                        href={item.buttonHref}
                        {...(isExternal(item.buttonHref) ? { target: "_blank", rel: "noreferrer" } : {})}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:gap-2"
                      >
                        {item.buttonLabel}
                        <ArrowRight className="size-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-12 max-w-xl mx-auto reveal">
          Cada módulo comparte datos en tiempo real. Una sola fuente de verdad para ventas, stock, clientes y finanzas.
        </p>
      </div>
    </section>
  );
}
